import { useEffect, useMemo, useState } from 'react';
import { Card, EmptyState, Icon, Modal, FormField, FormSelect, TextArea, PageHeader } from '@/components/ui';
import { agentApi } from '@/shared/api/agentApi';
import { useSipStore } from '../stores/sipStore';
import { toast } from '@/components/ui/Toast';
import { sortFollowups, isOverdue } from '../lib/followupSort';

interface CallbackRow {
  id: number;
  phone: string;
  name?: string;
  scheduled_at: string;
  reason?: string;
  status: string;
  source?: { type: 'call' | 'lead' | 'manual'; id: number | null; label: string };
}

interface Colleague { id: number; name: string }

export function Callbacks() {
  const [items, setItems] = useState<CallbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [transferTarget, setTransferTarget] = useState<CallbackRow | null>(null);
  const dial = useSipStore((s) => s.dial);

  // אפיון 5.1: מיון לפי מועד, כשמה שכבר עבר זמנו עולה לראש הרשימה.
  const sorted = useMemo(() => sortFollowups(items), [items]);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    const data = (await agentApi.callbacks()) as CallbackRow[];
    setItems(data);
    setLoading(false);
  }

  async function markDone(id: number) {
    try {
      await agentApi.updateCallback(id, { status: 'done' });
      toast('סומן כבוצע', 'ok');
      void load();
    } catch {
      toast('שגיאה בעדכון החזרה', 'danger');
    }
  }

  async function cancel(id: number) {
    try {
      await agentApi.deleteCallback(id);
      toast('החזרה בוטלה', 'ok');
      void load();
    } catch {
      toast('שגיאה בביטול החזרה', 'danger');
    }
  }

  if (loading) return <Card>טוען…</Card>;

  if (!items.length) {
    return <EmptyState icon="clock" title="אין שיחות מעקב" description="הוסף תזכורת מתוך שיחה פעילה." />;
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="שיחות מעקב" />
      {sorted.map((c) => {
        const late = isOverdue(c.scheduled_at);
        return (
        <Card key={c.id}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              // סימון ויזואלי לפולו-אפ באיחור: פס בצד הכרטיס, לא רק צבע טקסט.
              ...(late ? { borderInlineStart: '3px solid var(--danger)', paddingInlineStart: 13, marginInlineStart: -16 } : null),
            }}
          >
            <Icon name="clock" size={20} color={late ? 'var(--danger)' : 'var(--info)'} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span dir="ltr" style={{ fontSize: 'var(--t-lg)', fontWeight: 600 }}>
                  {c.phone}{c.name && <span style={{ color: 'var(--text-2)', fontWeight: 400 }}> · {c.name}</span>}
                </span>
                {late && (
                  <span
                    style={{
                      fontSize: 'var(--t-xs)', fontWeight: 600, whiteSpace: 'nowrap',
                      color: 'var(--on-danger, #fff)', background: 'var(--danger)',
                      borderRadius: 'var(--r-pill)', padding: '2px 8px',
                    }}
                  >
                    באיחור
                  </span>
                )}
                {c.source && (
                  <span
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 'var(--t-xs)', color: 'var(--text-2)',
                      background: 'var(--surface-2)', borderRadius: 'var(--r-1)',
                      padding: '2px 8px', whiteSpace: 'nowrap',
                    }}
                  >
                    <Icon name={c.source.type === 'call' ? 'phone' : c.source.type === 'lead' ? 'target' : 'edit'} size={11} />
                    {c.source.label}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 'var(--t-sm)', color: late ? 'var(--danger)' : 'var(--muted)', fontWeight: late ? 600 : undefined }}>
                {new Date(c.scheduled_at).toLocaleString('he-IL')} · {c.reason ?? '—'}
              </div>
            </div>
            <button
              type="button"
              className="btn primary"
              onClick={async () => {
                // JsSIP only — adding agentApi.dial() here originates a second
                // ARI leg to the same number (customer rings twice).
                try {
                  await dial(c.phone);
                } catch {
                  toast('שגיאה בחיוג', 'danger');
                }
              }}
            >
              <Icon name="phone" size={14} /> חייג
            </button>
            <button type="button" className="btn" onClick={() => markDone(c.id)}>
              סמן כבוצע
            </button>
            <button type="button" className="btn" onClick={() => setTransferTarget(c)} title="בקש העברה לנציג אחר">
              <Icon name="moveRight" size={14} />
            </button>
            <button type="button" className="btn" onClick={() => cancel(c.id)} title="בטל">
              <Icon name="trash" size={14} />
            </button>
          </div>
        </Card>
        );
      })}

      {transferTarget && (
        <TransferCallbackModal
          callback={transferTarget}
          onClose={() => setTransferTarget(null)}
          onDone={() => setTransferTarget(null)}
        />
      )}
    </div>
  );
}

function TransferCallbackModal({ callback, onClose, onDone }: { callback: CallbackRow; onClose: () => void; onDone: () => void }) {
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [userId, setUserId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void agentApi.colleagues().then((u) => setColleagues(u as Colleague[])).catch(() => {});
  }, []);

  async function submit() {
    if (!userId) { toast('בחר נציג', 'warn'); return; }
    setSaving(true);
    try {
      await agentApi.proposeCallbackTransfer(callback.id, Number(userId), note || undefined);
      toast('הבקשה נשלחה למפקח לאישור', 'ok');
      onDone();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשליחת הבקשה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="בקשת העברת פולו-אפ" size="sm"
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
        <button type="button" className="btn primary" onClick={submit} disabled={saving}>{saving ? 'שולח…' : 'שלח בקשה'}</button>
      </>}>
      <div className="stack">
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
          בקשה להעביר את הפולו-אפ <span dir="ltr">{callback.phone}</span> לנציג אחר. ההעברה בפועל מאושרת על ידי מפקח.
        </p>
        <FormField label="העבר לנציג">
          <FormSelect value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">בחר נציג…</option>
            {colleagues.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </FormSelect>
        </FormField>
        <FormField label="הערה (אופציונלי)">
          <TextArea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        </FormField>
      </div>
    </Modal>
  );
}
